import { type HTMLAttributes, type PropsWithChildren } from 'react';

import {
  Progress,
  ProgressGoal,
  ProgressGoalIndicator,
  ProgressIndicator,
  ProgressTrack,
} from '@/components/Progress';
import { Text } from '@/components/typography';
import { cn } from '@/lib/utils';
import { COLOR_AQUA_400 } from '@/styles/palette';

type Props = HTMLAttributes<HTMLDivElement> & {
  label: string;
  barLabel?: string;
  value: number;
  target: number;
  isMuted?: boolean;
  width: number;
};

const INSIDE_INDICATOR_THRESHOLD = 0.2;

const BenchmarksBar = ({
  className,
  label,
  barLabel,
  value,
  target,
  isMuted,
  children,
  width,
  ...rest
}: PropsWithChildren<Props>) => {
  // Render a compact version for when we have limited width
  const isCompact = width < 400;

  return (
    <div
      className={cn(
        'mx-2 flex flex-row opacity-100 transition-opacity duration-250 ease-in-out',
        isMuted && 'opacity-30',
        isCompact && 'flex-col-reverse',
        className,
      )}
      {...rest}
    >
      <Text className={cn('w-30 pt-1.5', isCompact && 'block w-full')} variant="caption1">
        {label}
      </Text>
      <Progress className="flex-1 gap-0" value={value * 100}>
        <ProgressTrack className="h-8 bg-gray-100">
          <ProgressIndicator className="bg-green-400">
            {value >= INSIDE_INDICATOR_THRESHOLD && (
              <div className="flex h-full min-w-0 flex-1 items-center justify-end px-2">
                <Text className={cn('truncate')} variant="caption2">
                  {barLabel}
                </Text>
              </div>
            )}
          </ProgressIndicator>
          {value < INSIDE_INDICATOR_THRESHOLD && (
            <Text
              className="absolute left-0 pl-3"
              variant="caption2"
              style={{ left: `${value * 100}%` }}
            >
              {barLabel}
            </Text>
          )}
          <ProgressGoal value={target * 100} shouldAnimate>
            <ProgressGoalIndicator orientation="above" style={{ color: COLOR_AQUA_400 }} />
          </ProgressGoal>
          {children}
        </ProgressTrack>
      </Progress>
    </div>
  );
};

export default BenchmarksBar;
